import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import type { CatalogueItem } from "@shared-core/data/catalogue-types";
import {
  fulfilmentBadge,
  tilePrice,
  type FulfilmentTone,
} from "@shared-core/domain/catalogue-presentation";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { Card } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { catalogueIcon } from "./catalogueIcons";
import { ArrowRight, Pencil, Plus, Star, Trash2 } from "lucide-react";

// The catalogue item grid — tiles that open the configurator in "use" mode
// (ordering) or "edit" mode (managing). Shared by the ordering page and the
// settings catalogue editor so the two render identically.

const TONE_VARIANT: Record<FulfilmentTone, "neutral" | "success" | "warning"> =
  {
    made: "neutral",
    bought: "warning",
  };

/**
 * One grid tile. The browse page passes shared-core `CatalogueItemTile`s
 * (which carry the extras); the settings editor wraps its plain items as
 * `{ item }` — the extras are then absent and their affordances don't render.
 */
export interface CatalogueGridTile {
  item: CatalogueItem;
  /** Owning catalogue's name — rendered when browsing across catalogues. */
  catalogueName?: string;
  /** Server-computed; falls back to inspecting item.fields when absent. */
  needsSetup?: boolean;
  isFavourite?: boolean;
  /** Server-computed; falls back to item.fields.length when absent. */
  fieldCount?: number;
  /**
   * Server-resolved "from £X" price (cheapest cached manufacturing cost,
   * converted via the price group's margin). Absent (not just `null`) means
   * this caller never fetched cost-derived pricing — e.g. the settings
   * catalogue editor grid — and the tile always reads "Priced on setup".
   */
  indicativePricePence?: number | null;
}

export function CatalogueItemGrid({
  tiles,
  manage,
  onRemove,
  removableItemIds,
  onEdit,
  onNew,
  onToggleFavourite,
  itemSearch,
}: {
  itemSearch?: string;
  tiles: CatalogueGridTile[];
  manage: boolean;
  /**
   * When provided, tiles for removable items show a control that takes the
   * item out of the caller's working copy.
   */
  onRemove?: (id: string) => void;
  /**
   * The ids `onRemove` may actually act on. Defaults to none, so a caller that
   * forgets it gets no remove controls rather than controls that do nothing.
   *
   * TEMPORARY, and the entire reason this prop exists. There is no delete or
   * retire endpoint for a saved catalogue item, so dropping one from the
   * working copy changed nothing anywhere: the editor's save loop sends only
   * new and modified items, the post-save refetch brought the removed item
   * straight back, and the user was told "Saved — live now". The control lied.
   * Until retire-an-item ships (backend action + route + adapter method), only
   * items added in this session — which exist nowhere but the working copy —
   * can be taken out.
   *
   * To undo when retire lands: delete this prop and the note below, and let
   * every tile render the control again.
   */
  removableItemIds?: ReadonlySet<string>;
  /** When provided, a tile edits in place (no navigation) — used by the editor. */
  onEdit?: (id: string) => void;
  /** When provided, a ghost "add item" tile sits at the end of the grid. */
  onNew?: () => void;
  /** When provided, tiles carry a favourite star toggle (browse mode). */
  onToggleFavourite?: (itemId: string, next: boolean) => void;
}) {
  if (tiles.length === 0 && !onNew) {
    return (
      <Card className="p-12 text-center text-muted-foreground">
        No items in this catalogue yet.
      </Card>
    );
  }
  const canRemove = (itemId: string) =>
    !!onRemove && !!removableItemIds?.has(itemId);
  // Say why the control is missing. A tile that silently loses its bin reads
  // as a rendering bug, and on a grid of saved items the affordance vanishes
  // entirely with nothing to explain it.
  const hasUnremovableItems =
    !!onRemove && tiles.some((t) => !canRemove(t.item.id));

  return (
    <div className="space-y-3">
      {hasUnremovableItems && (
        <p className="text-xs text-muted-foreground">
          Items already saved to this catalogue can&apos;t be removed here yet —
          you can only take out items you add in this session, before saving.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((tile) => (
          <CatalogueTile
            key={tile.item.id}
            tile={tile}
            manage={manage}
            onRemove={canRemove(tile.item.id) ? onRemove : undefined}
            onEdit={onEdit}
            onToggleFavourite={onToggleFavourite}
            itemSearch={itemSearch}
          />
        ))}
        {onNew && <NewItemTile onClick={onNew} />}
      </div>
    </div>
  );
}

function NewItemTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex min-h-[12rem] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-5 text-muted-foreground transition-colors hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]"
    >
      <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
        <Plus className="size-5" />
      </div>
      <div className="font-medium">New item</div>
      <div className="text-xs text-muted-foreground">
        From a product type or another item
      </div>
    </button>
  );
}

function CatalogueTile({
  tile,
  manage,
  onRemove,
  onEdit,
  onToggleFavourite,
  itemSearch,
}: {
  itemSearch?: string;
  tile: CatalogueGridTile;
  manage: boolean;
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
  onToggleFavourite?: (itemId: string, next: boolean) => void;
}) {
  const { item } = tile;
  const Icon = catalogueIcon(item.icon);
  const badge = fulfilmentBadge(item);
  const price = tilePrice(
    item,
    tile.needsSetup,
    tile.indicativePricePence ?? null,
  );
  const isFavourite = tile.isFavourite ?? false;
  const fieldCount = tile.fieldCount ?? item.fields.length;

  return (
    <Card className="relative p-5 flex flex-col gap-4 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-[color:var(--color-primary)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-muted-foreground shrink-0">
            <Icon className="size-5" />
          </div>
          <div className="min-w-0">
            <h3 className="break-words">
              {onEdit ? (
                <button
                  className="text-left after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none"
                  onClick={() => onEdit(item.id)}
                >
                  {item.name}
                </button>
              ) : (
                <Link
                  className="after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none"
                  to={`${paths.catalogueItem({ itemId: item.id })}${itemSearch ? `?${itemSearch}` : ""}`}
                >
                  {item.name}
                </Link>
              )}
            </h3>
            <div className="text-xs text-muted-foreground break-words">
              {item.productLabel}
              {tile.catalogueName && (
                <div className="mt-1 font-medium">{tile.catalogueName}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onToggleFavourite && (
            <button
              aria-label={
                isFavourite
                  ? `Unfavourite ${item.name}`
                  : `Favourite ${item.name}`
              }
              aria-pressed={isFavourite}
              className={
                "relative z-10 flex size-8 items-center justify-center rounded-md shrink-0 focus-visible:outline-2 focus-visible:outline-primary " +
                (isFavourite
                  ? "text-[color:var(--color-primary)]"
                  : "text-muted-foreground hover:text-[color:var(--color-primary)]")
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavourite(item.id, !isFavourite);
              }}
            >
              <Star
                className={isFavourite ? "size-4 fill-current" : "size-4"}
              />
            </button>
          )}
          {onRemove && (
            <button
              aria-label={`Remove ${item.name}`}
              className="relative z-10 flex size-8 items-center justify-center rounded-md text-muted-foreground hover:text-[color:var(--color-error)]"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.id);
              }}
            >
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant={TONE_VARIANT[badge.tone]}>{badge.label}</Badge>
        {manage && item.vatTreatment == null && (
          <Badge variant="warning">VAT not set</Badge>
        )}
      </div>

      <div className="text-sm text-muted-foreground min-h-[2.5rem]">
        {item.specSummary ??
          (fieldCount > 0
            ? `${fieldCount} option${fieldCount === 1 ? "" : "s"} to set`
            : "Order again — nothing to configure")}
        {item.note && (
          <span className="ml-1 text-muted-foreground">· {item.note}</span>
        )}
      </div>

      <div className="flex items-end justify-between gap-2 mt-auto">
        <div>
          {price.kind === "from" ? (
            <>
              <div className="text-lg font-semibold">
                from {formatCurrencyGbp(price.amount)}
              </div>
              <div className="text-xs text-muted-foreground">
                for {price.quantity.toLocaleString()}
              </div>
            </>
          ) : (
            <div className="text-sm font-medium text-muted-foreground">
              Priced on setup
            </div>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-sm text-[color:var(--color-primary)] font-medium">
          {manage ? (
            <>
              Edit <Pencil className="size-3.5" />
            </>
          ) : (
            <>
              Configure &amp; price <ArrowRight className="size-4" />
            </>
          )}
        </span>
      </div>
    </Card>
  );
}
